import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { usePurchaseOrderProductsSyncInformationsDialog } from '@/features/purchase-order/hooks/use-purchase-order-products-sync-informations-dialog';
import { cn } from '@/lib/utils';
import type { PurchaseOrderProductsSyncInformationsProps } from './types/purchase-order-products-sync-informations.types';

const SYNC_INFORMATION_SKELETON_SECTIONS = [
  {
    id: 'sync-section-primary',
    fieldKeys: [
      'field-1',
      'field-2',
      'field-3',
      'field-4',
      'field-5',
      'field-6',
    ],
  },
  {
    id: 'sync-section-secondary',
    fieldKeys: ['field-7', 'field-8', 'field-9'],
  },
] as const;

export function PurchaseOrderProductsSyncInformationsDialog({
  data,
}: Pick<PurchaseOrderProductsSyncInformationsProps, 'data'>) {
  const { t } = useTranslation('purchaseOrder');
  const { dialog } = data;
  const {
    sections,
    isPending,
    isError,
    filteredSelectedFieldKeys,
    targetedProductsLabel,
    isConfirmDisabled,
    toggleField,
  } = usePurchaseOrderProductsSyncInformationsDialog({ data });

  return (
    <Dialog onOpenChange={dialog.onOpenChange} open={dialog.isOpen}>
      <DialogContent
        className='overflow-hidden rounded-xl p-0 sm:max-w-[620px]'
        showCloseButton={false}
      >
        <div className='max-h-[85vh] overflow-y-auto px-8 py-7'>
          <DialogHeader className='mb-6 flex-row items-start justify-between gap-4 text-left'>
            <div className='space-y-2'>
              <DialogTitle className='text-[22px] leading-tight'>
                {t('syncInformationsFieldsTitle')}
              </DialogTitle>
              <DialogDescription className='text-[16px] leading-none'>
                {targetedProductsLabel}
              </DialogDescription>
            </div>

            <DialogClose asChild>
              <button
                aria-label={t('close', { ns: 'common', defaultValue: 'Close' })}
                className='-mt-2 -mr-2 inline-flex size-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted'
                type='button'
              >
                <X className='size-7' />
              </button>
            </DialogClose>
          </DialogHeader>

          {/* Skeleton loading state */}
          {isPending ? (
            <div className='grid gap-y-6'>
              {SYNC_INFORMATION_SKELETON_SECTIONS.map((section) => (
                <div className='space-y-3' key={section.id}>
                  <Skeleton className='h-6 w-40' />
                  <div className='grid gap-3 sm:grid-cols-2'>
                    {section.fieldKeys.map((fieldKey) => (
                      <div
                        className='flex h-[44px] items-center gap-3 rounded-[10px] border px-3'
                        key={fieldKey}
                      >
                        <Skeleton className='size-4 rounded-sm' />
                        <Skeleton className='h-4 w-32' />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* Error state */}
          {isError ? (
            <Alert className='border-destructive/20 bg-destructive/5 text-destructive'>
              <AlertDescription className='text-destructive'>
                {t('syncInformationsFieldsError')}
              </AlertDescription>
            </Alert>
          ) : null}

          {/* Content */}
          {isPending || isError ? null : (
            <div className='space-y-6'>
              {sections.map(([section, fields]) => (
                <section className='space-y-3' key={section}>
                  <h3 className='font-semibold text-[18px] leading-none'>
                    {t(`syncInformationsSections.${section}`)}
                  </h3>
                  <div className='grid gap-3 sm:grid-cols-2'>
                    {fields.map((field) => {
                      const isChecked = filteredSelectedFieldKeys.includes(
                        field.key,
                      );

                      return (
                        <label
                          className={cn(
                            'flex min-h-[44px] cursor-pointer items-center gap-4 rounded-[10px] border bg-background px-3 py-2 shadow-xs transition-colors',
                            isChecked
                              ? 'border-primary/35'
                              : 'border-input hover:border-primary/25',
                          )}
                          htmlFor={`sync-field-${field.key}`}
                          key={field.key}
                        >
                          <Checkbox
                            checked={isChecked}
                            id={`sync-field-${field.key}`}
                            onCheckedChange={(checked) =>
                              toggleField(field.key, checked)
                            }
                          />
                          <span className='min-w-0 text-[16px] text-foreground/90 leading-none'>
                            {t(`syncInformationsFields.${field.key}`)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}

          <DialogFooter className='mt-8 gap-4'>
            <DialogClose asChild>
              <Button
                className='h-12 min-w-[142px] text-base'
                type='button'
                variant='outline'
              >
                {t('cancel', { ns: 'common' })}
              </Button>
            </DialogClose>
            <Button
              className='h-12 min-w-[184px] text-base'
              disabled={isConfirmDisabled}
              onClick={dialog.onConfirm}
              type='button'
            >
              {dialog.isSubmitting
                ? t('syncInformationsSubmittingBtn')
                : t('syncInformationsConfirmBtn')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
